import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import analyticsApi from '~/apis/analytics';
import { StorageModeFilter, useStorageModeFilter } from '~/components/common';
import { IErrorsResponse } from '~/utils/interface/analytics';

const ErrorLogs = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<IErrorsResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const storageMode = useStorageModeFilter();

  useEffect(() => {
    analyticsApi
      .getErrors(storageMode)
      .then((res) => setData(res.data.data))
      .catch(() => setError(t('pages.errorLogs.loadError')))
      .finally(() => setIsLoading(false));
  }, [t, storageMode]);

  return (
    <div>
      <h1 className='text-xl font-semibold text-foreground'>{t('pages.errorLogs.title')}</h1>
      <p className='mt-1 text-sm text-muted-foreground'>{t('pages.errorLogs.description')}</p>
      <StorageModeFilter />

      {isLoading && <p className='mt-6 text-sm text-muted-foreground'>{t('common.loading')}</p>}
      {error && <p className='mt-6 text-sm text-destructive'>{error}</p>}

      {data && (
        <div className='mt-6 flex flex-col gap-6'>
          <div className='rounded-lg border border-border bg-card p-4 sm:p-6'>
            <h2 className='mb-4 text-base font-semibold text-foreground'>
              {t('pages.errorLogs.topErrors')}
            </h2>
            {data.summary.length === 0 ? (
              <p className='text-sm text-muted-foreground'>{t('pages.errorLogs.noErrors')}</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='w-full min-w-[420px] text-left text-sm'>
                  <thead>
                    <tr className='border-b border-border text-muted-foreground'>
                      <th className='py-2 pr-4 font-medium'>{t('pages.errorLogs.event')}</th>
                      <th className='py-2 pr-4 font-medium'>{t('pages.errorLogs.errorCode')}</th>
                      <th className='py-2 font-medium'>{t('pages.errorLogs.count')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.summary.map((row, i) => (
                      <tr key={i} className='border-b border-border last:border-0'>
                        <td className='py-2 pr-4 text-foreground'>{row.eventName}</td>
                        <td className='py-2 pr-4 text-foreground'>{row.errorCode}</td>
                        <td className='py-2 text-foreground'>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className='rounded-lg border border-border bg-card p-4 sm:p-6'>
            <h2 className='mb-4 text-base font-semibold text-foreground'>
              {t('pages.errorLogs.mostRecent')}
            </h2>
            {data.recent.length === 0 ? (
              <p className='text-sm text-muted-foreground'>{t('pages.errorLogs.noErrors')}</p>
            ) : (
              <div className='max-h-[500px] overflow-auto'>
                <table className='w-full min-w-[700px] text-left text-sm'>
                  <thead>
                    <tr className='border-b border-border text-muted-foreground'>
                      <th className='py-2 pr-4 font-medium'>{t('pages.errorLogs.time')}</th>
                      <th className='py-2 pr-4 font-medium'>{t('pages.errorLogs.event')}</th>
                      <th className='py-2 pr-4 font-medium'>{t('pages.errorLogs.screen')}</th>
                      <th className='py-2 pr-4 font-medium'>{t('pages.errorLogs.errorCode')}</th>
                      <th className='py-2 pr-4 font-medium'>{t('pages.errorLogs.appVersion')}</th>
                      <th className='py-2 font-medium'>{t('pages.errorLogs.device')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((row, i) => (
                      <tr key={i} className='border-b border-border last:border-0'>
                        <td className='py-2 pr-4 whitespace-nowrap text-foreground'>
                          {new Date(row.occurred_at).toLocaleString()}
                        </td>
                        <td className='py-2 pr-4 text-foreground'>{row.event_name}</td>
                        <td className='py-2 pr-4 text-foreground'>{row.screen ?? '-'}</td>
                        <td className='py-2 pr-4 text-foreground'>{row.error_code ?? '-'}</td>
                        <td className='py-2 pr-4 text-foreground'>{row.app_version ?? '-'}</td>
                        <td className='py-2 text-foreground'>
                          {row.device_model ?? '-'} ({row.os_version ?? '-'})
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorLogs;
